import { Grid, Typography, Button, Divider, Chip } from '@mui/material'
import { useGetApplicationsByTalentQuery } from '../../../Redux/api/applicationApi'
import { skipToken } from '@reduxjs/toolkit/query'
import { KaziAvatar } from '../../../Shared Components/Kazi/KaziAvatar'
import { formatUserName } from '../../../Utils/String'
import { ReactNode, useEffect, useState } from 'react'
import { TalentViewApplicationModal } from './TalentViewApplicationModal'
import { useAppSelector } from '../../../Redux/store'
import { FactCheckOutlined } from '@mui/icons-material'
import { IApplicationAnswer } from '../../../Types/Application'
import { useGetManyJobsQuery } from '../../../Redux/api/jobApi'
import { useGetTalentQuery } from '../../../Redux/api/talentApi'
import { useSearchParams } from 'react-router-dom'
import { useKaziNavigate } from '../../../Hooks/useKaziNavigate'

export const TalentApplications = () => {
    /* ******************** Hooks ******************** */
    const { navigateWithBack } = useKaziNavigate()
    const userId = useAppSelector( state => state.Global.userId)
    const [searchParams, setSearchParams] = useSearchParams();
    const locator = searchParams.get('application');
    const {
        data: talentData, 
        isLoading: talentDataLoading, 
        isFetching: talentDataFetching
    } = useGetTalentQuery(userId || skipToken);
    const {data: apps, isLoading: appsLoading, isFetching: appsFetching} = useGetApplicationsByTalentQuery(userId || skipToken);
    const {data: invitejobs, isLoading: invitejobsLoading, isFetching: invitejobsFetching} = useGetManyJobsQuery((talentData?.job_invites.map(t => t.job_locator) ) || skipToken);
    const [viewApplication, setViewApplication] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<{
        client_locator: string; 
        client_name: string;
        client_title: string;
        client_avatar: ReactNode; 
        job_title: string;
        job_locator: string;
        job_type: string; 
        questions: {id: number, question: string}[];
        form: IApplicationAnswer[] | undefined;
    } | undefined>(undefined)
    /* ******************** Variables ******************** */
    /* ******************** Functions ******************** */
    const handleView = (app: any) => {
        if (locator !== app.locator) setSearchParams({ application: app.locator})
        setSelectedApplication({
            client_locator: app?.client?.locator,
            client_name: getName(app?.client),
            client_title: app?.client?.title,
            client_avatar: getAvatar(app?.client),
            job_locator: app?.job.locator,
            job_title: app?.job.title,
            job_type: app?.job.type,
            questions: app?.job?.questions,
            form: app?.application_form
        })
        setViewApplication(true);
    }
    const getAvatar = (client: any) => {
        return (
            <KaziAvatar 
                locator={client?.locator} 
                first_name={client?.first_name} 
                last_name={client?.last_name} 
                width='32px'
            />
        )
    }
    const getName = (talent: any) => {
        return formatUserName(talent?.first_name, talent?.last_name)
    }
    const handleReview = () => {};
    /* ******************** Effects ******************** */
    useEffect(()=>{
        if (locator) {
            const app = apps?.find( app => app.locator === locator);
            handleView(app);
        }
    },[])
    /* ******************** JSX ******************** */
    return (
        <>
            {
                <Grid  display={'flex'} flexDirection={'column'} marginBottom={8}>
                        <Typography variant='lora-headline-small' color={'text.primary'} marginBottom={'28px'}>Job applications</Typography>
                        {
                            apps && apps.length > 0 ?
                            <Grid display={'flex'} flexDirection={'column'} marginLeft={2} gap={2}>
                                {
                                    apps?.map((app, idx) => (    
                                        <>                                 
                                            <Grid xs display={'flex'} flexDirection={'row'} key={app.client?.locator + idx}>
                                                <Grid  flex={1} display={'flex'} flexDirection={'row'} gap={2}>
                                                    <Grid display={'flex'} flexDirection={'column'}> 
                                                        {getAvatar(app.client)}
                                                    </Grid>
                                                    <Grid  display={'flex'} flexDirection={'column'}>
                                                        <Typography variant='body-medium'>{getName(app.client)}</Typography>
                                                        <Typography variant='body-smaller'color={'black.300'}>{app.job?.title ?? 'N/A'}</Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid flex={1} justifyContent={'flex-end'} display={'flex'} flexDirection={'row'} gap={'16px'} alignItems={'center'}>
                                                    {/**TODO: Add logic to show new applications*/}
                                                    {
                                                        app.job?.talent_locator === userId && 
                                                        <Chip label={'ACCEPTED'} size={'small'} sx={{backgroundColor: 'green.50', color: 'green.300'}} />
                                                    }
                                                    <Button variant='text' sx={{ fontWeight: 'bold'}} onClick={() => handleView(app)}>View application</Button>
                                                    {app.status === 'accepted' && <Button variant='contained' disabled={app.job?.talent_locator?.length > 0} onClick={() => handleReview()}>Review</Button>}
                                                </Grid>
                                            </Grid> 
                                            <Divider />
                                        </>   
                                    ))
                                }
                            </Grid>:                   
                            <Grid  display={'flex'} flexDirection={'column'}  alignItems={'center'} gap={'20px'}>
                                <FactCheckOutlined sx={{fontSize: '100px', color: 'green.200'}}/>
                                <Typography variant='body-large'>No job applications</Typography>
                            </Grid>
                        }
                </Grid>
            }
            {
                //invites && 
                <Grid  display={'flex'} flexDirection={'column'} >
                        <Typography variant='lora-headline-small' color={'text.primary'} marginBottom={'28px'}>Job application invites</Typography>
                        {
                            invitejobs && invitejobs.length > 0 ?                            
                            <Grid display={'flex'} flexDirection={'row'} marginLeft={2}>
                                {
                                    // TODO: show invites
                                    invitejobs.map((invite, idx) => (    
                                        <>                                 
                                            <Grid xs display={'flex'} flexDirection={'row'} key={invite.client?.locator + idx}>
                                                <Grid  flex={1} display={'flex'} flexDirection={'row'} gap={2}>
                                                    <Grid display={'flex'} flexDirection={'column'}> 
                                                        {getAvatar(invite.client)}
                                                    </Grid>
                                                    <Grid  display={'flex'} flexDirection={'column'}>
                                                        <Typography variant='body-medium'>{getName(invite.client)}</Typography>
                                                        <Typography variant='body-smaller'color={'black.300'}>{invite.title ?? 'N/A'}</Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid flex={1} justifyContent={'flex-end'} display={'flex'} flexDirection={'row'} gap={'16px'} alignItems={'center'}>
                                                    <Button variant='text' sx={{ fontWeight: 'bold'}} onClick={() => navigateWithBack(`/marketplace/posting/${invite.locator}`)}>View job post</Button>
                                                    {/* <Button variant='contained'  >Review application</Button> */}
                                                </Grid>
                                            </Grid> 
                                            <Divider />
                                        </>   
                                    ))
                                }
                            </Grid> :                        
                            <Grid  display={'flex'} flexDirection={'column'}  alignItems={'center'} gap={'20px'}>
                                <FactCheckOutlined sx={{fontSize: '100px', color: 'green.200'}}/>
                                <Typography variant='body-large'>No job application invites</Typography>
                            </Grid>
                        }
                </Grid>
            }
            <TalentViewApplicationModal 
                open={viewApplication} 
                setOpen={setViewApplication} 
                applicationInfo={selectedApplication!} 
            />
        </>
    )
}